import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, Subscription, finalize, first, firstValueFrom, merge, switchMap, tap } from 'rxjs';
import { ScreenService } from 'src/app/core/screen.service';
import { Filter } from 'src/app/models/common/filter';
import { OrdersListDataSource } from './orders-list.datasource';
import { OrderService } from 'src/app/data/order.service';
import { OrderItemStatus } from 'src/app/models/enums/order-item-status';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { OrdersDto } from 'src/app/models/dtos/orders/orders-dto';
import { Messages, Permission } from 'src/app/common/enums';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { OrdersListFilterService } from '../../services/orders-list-filter-service';
import moment from 'moment';
import { EditOrderItemModalComponent } from './edit-order-item-modal/edit-order-item-modal.component';
import { AuthService } from 'src/app/core/authentication/auth.service';

const DEFAULT_SORT_FIELD = 'default';

@Component({
  selector: 'app-orders-list',
  templateUrl: './orders-list.component.html',
  styleUrls: ['./orders-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrdersListDataSource, { provide: FilterService, useClass: OrdersListFilterService }],
  standalone: false
})
export class OrdersListComponent implements AfterViewInit, OnInit, OnDestroy {
  screenService = inject(ScreenService);
  private orderService = inject(OrderService);
  private snackbar = inject(SnackBarService);
  private dialog = inject(MatDialog);
  dataSource = inject(OrdersListDataSource);
  private translateService = inject(TranslateService);
  private spinner = inject(NgxSpinnerService);
  private filterService = inject(FilterService);
  private authService = inject(AuthService);

  displayedColumns = [
    'submissionDate',
    'submitterLastName',
    'employerName',
    'employerObjectName',
    'positionExpectationName',
    'startDate',
    'employeesNumber',
    'actions',
  ];

  filters: Filter[] = [];

  private readonly MAX_DIFFERENCE_DAYS = 5;

  private _areFiltersExpanded: boolean;
  public get areFiltersExpanded(): boolean {
    return this._areFiltersExpanded;
  }
  public set areFiltersExpanded(value: boolean) {
    if (this._areFiltersExpanded !== value) {
      this._areFiltersExpanded = value;
    }
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private subscriptions: Subscription[] = [];

  constructor() { }

  ngOnInit(): void {
    this.filterService.filters$.subscribe((filters) => {
      this.filterData(filters);
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  ngAfterViewInit(): void {
    this.sort.sort({ id: DEFAULT_SORT_FIELD, start: 'asc' } as MatSortable);

    this.subscriptions.push(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0)));

    this.subscriptions.push(merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange).subscribe(() => this.fetchOrders()));

    this.subscriptions.push(
      this.dataSource.loading$.subscribe({
        next: (isLoading) => (isLoading ? this.spinner.show() : this.spinner.hide()),
        complete: () => this.spinner.hide(),
      }),
    );

    this.fetchOrders();
  }

  toggleFiltersPanel() {
    this.areFiltersExpanded = !this.areFiltersExpanded;
  }

  async resetFilters(): Promise<void> {
    await firstValueFrom(this.filterService.resetFilters());
  }

  async filterData(filters: Filter[]): Promise<void> {
    this.filters = filters;
    this.fetchOrders();
  }

  hasArchiveRestorePermission() {
    return this.authService.hasPermission(Permission.ArchiveRestoreOrderItem);
  }

  hasEditPermission() {
    return this.authService.hasPermission(Permission.EditOrderItem);
  }

  archiveButtonVisible = (orderItemStatusId: number): boolean => orderItemStatusId === OrderItemStatus.Active && this.hasArchiveRestorePermission();
  restoreButtonVisible = (orderItemStatusId: number): boolean => orderItemStatusId === OrderItemStatus.Archived && this.hasArchiveRestorePermission();

  async archiveOrderItem(orderItemId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.orderService.archiveOrderItem(orderItemId).pipe(
        first(),
        tap((_) => this.fetchOrders()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyArchivedOrder)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.ArchiveOrderItemTitle,
            message: Messages.ArchiveOrderItemMessage,
            confirmButtonLabel: this.translateService.instant(Messages.ArchiveOrderItemConfirmButton),
            dismissButtonLabel: this.translateService.instant(Messages.OrderDeclineButton),
          },
        })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  async restoreOrderItem(orderItemId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.orderService.restoreOrderItem(orderItemId).pipe(
        first(),
        tap((_) => this.fetchOrders()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyRestoredOrder)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.RestoreOrderItemTitle,
            message: Messages.RestoreOrderItemMessage,
            confirmButtonLabel: this.translateService.instant(Messages.RestoreOrderItemConfirmButton),
            dismissButtonLabel: this.translateService.instant(Messages.OrderDeclineButton),
          },
        })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  editButtonVisible(element: OrdersDto) {
    return this.canEdit(element) && this.hasEditPermission();
  }

  editOrderItem(element: OrdersDto) {
    if (!this.canEdit(element)) {
      this.snackbar.openErrorSnackBar(Messages.StartDateIsNotValidToEdit);
      return;
    }

    const dialogRef = this.dialog.open(EditOrderItemModalComponent, {
      data: {
        orderItemId: element.Id,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchOrders();
      }
    });
  }

  canEdit(element: OrdersDto): boolean {
    const today = moment.utc();
    const minEditableDate = today.clone().add(this.MAX_DIFFERENCE_DAYS, 'days');
    const startDate = moment.utc(element.StartDate);

    return startDate.isAfter(minEditableDate, 'day');
  }

  hasAnyAction(order: OrdersDto): boolean {
    return this.restoreButtonVisible(order.OrderItemStatusId) || this.archiveButtonVisible(order.OrderItemStatusId) || this.editButtonVisible(order);
  }

  private fetchOrders(resetPage: boolean = false) {
    if (resetPage) {
      this.paginator.pageIndex = 0;
    }

    this.dataSource.fetchOrders(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active, this.sort.direction, this.filters);
  }
}
